import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  useToast,
  Box,
  FormHelperText,
  Select,
  HStack,
  InputGroup,
  InputLeftAddon,
  Heading,
  Icon,
  keyframes,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { gql, useMutation } from "@apollo/client";
import { getColor } from "../brandConfig";
import { PhoneIcon, CheckIcon } from "@chakra-ui/icons";

// Common country codes for phone numbers
const COUNTRY_CODES = [
  { code: "+61", country: "Australia", flag: "🇦🇺", placeholder: "412 345 678" },
  { code: "+1", country: "United States", flag: "🇺🇸", placeholder: "555 123 4567" },
  { code: "+1", country: "Canada", flag: "🇨🇦", placeholder: "555 123 4567" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧", placeholder: "7700 900123" },
  { code: "+33", country: "France", flag: "🇫🇷", placeholder: "6 12 34 56 78" },
  { code: "+49", country: "Germany", flag: "🇩🇪", placeholder: "30 12345678" },
  { code: "+81", country: "Japan", flag: "🇯🇵", placeholder: "90 1234 5678" },
  { code: "+86", country: "China", flag: "🇨🇳", placeholder: "139 0013 8000" },
  { code: "+91", country: "India", flag: "🇮🇳", placeholder: "98765 43210" },
  { code: "+64", country: "New Zealand", flag: "🇳🇿", placeholder: "21 123 4567" },
  { code: "+65", country: "Singapore", flag: "🇸🇬", placeholder: "8123 4567" },
  { code: "+62", country: "Indonesia", flag: "🇮🇩", placeholder: "812 3456 789" },
  { code: "+60", country: "Malaysia", flag: "🇲🇾", placeholder: "12 345 6789" },
  { code: "+66", country: "Thailand", flag: "🇹🇭", placeholder: "81 234 5678" },
  { code: "+63", country: "Philippines", flag: "🇵🇭", placeholder: "917 123 4567" },
];

const REQUEST_AUTH_SMS = gql`
  mutation RequestAuthViaSMS($input: AuthViaSMSInput!) {
    requestAuthViaSMS(input: $input)
  }
`;

const VERIFY_AUTH_SMS = gql`
  mutation VerifyAuthViaSMS($input: VerifySMSInput!) {
    verifyAuthViaSMS(input: $input)
  }
`;

interface LoginWithSmsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
}

export const LoginWithSmsModal: React.FC<LoginWithSmsModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]); // Default to Australia
  const [localPhoneNumber, setLocalPhoneNumber] = useState("");
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState("");
  const [verificationWords, setVerificationWords] = useState("");
  const [smsSent, setSmsSent] = useState(false);
  const toast = useToast();

  const [requestAuthSms, { loading: requestLoading }] = useMutation(REQUEST_AUTH_SMS);
  const [verifyAuthSms, { loading: verifyLoading }] = useMutation(VERIFY_AUTH_SMS);

  const handleCountryChange = (countryCode: string) => {
    const country = COUNTRY_CODES.find(c => c.code === countryCode);
    if (country) {
      setSelectedCountry(country);
      // Clear local number when changing countries to avoid confusion
      setLocalPhoneNumber("");
    }
  };

  // Function to clean verification words - remove extra spaces and normalize
  const cleanVerificationWords = (input: string): string => {
    return input
      .toLowerCase() // Convert to lowercase
      .trim() // Remove leading and trailing spaces
      .replace(/\s+/g, " "); // Replace multiple spaces with single space
  };

  // Format phone number when country or local number changes
  useEffect(() => {
    if (!localPhoneNumber.trim()) {
      setFormattedPhoneNumber("");
      return;
    }

    let cleanedLocal = localPhoneNumber.trim();
    
    // Remove any spaces, dashes, parentheses
    cleanedLocal = cleanedLocal.replace(/[\s\-\(\)]/g, "");
    
    // Remove any existing plus sign or country code if user accidentally included it
    if (cleanedLocal.startsWith("+")) {
      cleanedLocal = cleanedLocal.substring(1);
    }
    
    // If it's Australian number and starts with 0, remove the 0
    if (selectedCountry.code === "+61" && cleanedLocal.startsWith("0")) {
      cleanedLocal = cleanedLocal.substring(1);
    }
    
    // Combine country code with cleaned local number
    const formatted = selectedCountry.code + cleanedLocal;
    
    setFormattedPhoneNumber(formatted);
  }, [localPhoneNumber, selectedCountry]);

  const handleRequestAuthSms = async () => {
    if (!formattedPhoneNumber || formattedPhoneNumber.length < 8) {
      toast({
        title: "Error",
        description: "Please enter a valid phone number",
        status: "error",
        duration: 3000,
      });
      return;
    }
    
    try {
      const { data } = await requestAuthSms({
        variables: {
          input: { 
            phoneNumber: formattedPhoneNumber,
          }
        }
      });

      if (data.requestAuthViaSMS) {
        setSmsSent(true);
        toast({
          title: "Verification SMS sent",
          description: "Please check your phone for the verification words",
          status: "success",
          duration: 5000,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send verification SMS",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleVerifyAuthSms = async () => {
    const cleanedWords = cleanVerificationWords(verificationWords);
    
    if (!cleanedWords.trim()) {
      toast({
        title: "Error",
        description: "Please enter the verification words",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      const { data } = await verifyAuthSms({
        variables: {
          input: {
            phoneNumber: formattedPhoneNumber,
            words: cleanedWords
          }
        }
      });

      if (data.verifyAuthViaSMS) {
        localStorage.setItem("auth_token", data.verifyAuthViaSMS);
        toast({
          title: "Success!",
          description: "You have been successfully logged in",
          status: "success",
          duration: 3000,
        });
        
        if (onLoginSuccess) {
          onLoginSuccess();
        } else {
          onClose();
          window.location.reload();
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Verification failed",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleClose = () => {
    setLocalPhoneNumber("");
    setVerificationWords("");
    setSmsSent(false);
    setSelectedCountry(COUNTRY_CODES[0]); // Reset to Australia
    onClose();
  };

  // Add gradient animation
  const gradientAnimation = keyframes`
    0% { background-position: 0% 50% }
    50% { background-position: 100% 50% }
    100% { background-position: 0% 50% }
  `;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay 
        bg="blackAlpha.300" 
        backdropFilter="blur(10px)"
      />
      <ModalContent 
        mx={4}
        borderRadius="2xl"
        overflow="hidden"
        boxShadow={`0 25px 50px -12px ${getColor("primary")}40`}
        border="1px solid"
        borderColor={getColor("border.light")}
        position="relative"
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: `linear-gradient(90deg, ${getColor("primary")}, ${getColor("secondary")}, ${getColor("accent")})`,
          backgroundSize: "200% 200%",
          animation: `${gradientAnimation} 3s ease infinite`,
        }}
      >
        <ModalHeader 
          pb={2}
          pt={8}
          px={8}
          bg={`linear-gradient(135deg, ${getColor("background.light")} 0%, ${getColor("background.card")} 100%)`}
        >
          <VStack spacing={2} align="center">
            <Box
              w="16"
              h="16"
              borderRadius="full"
              bg={`linear-gradient(135deg, ${getColor("primary")} 0%, ${getColor("secondary")} 100%)`}
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow={`0 10px 25px ${getColor("primary")}30`}
              animation={`${gradientAnimation} 4s ease infinite`}
              backgroundSize="200% 200%"
            >
              <Icon as={PhoneIcon} w="6" h="6" color="white" />
            </Box>
            <Heading size="lg" color={getColor("primary")} textAlign="center">
              {!smsSent ? "Secure Login" : "Verify Access"}
            </Heading>
            <Text color={getColor("text.secondary")} textAlign="center" fontSize="sm">
              {!smsSent 
                ? "Enter your phone number for secure SMS verification" 
                : "Check your phone for the verification code"
              }
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton 
          top={4} 
          right={4}
          color={getColor("text.muted")}
          _hover={{ 
            color: getColor("primary"),
            bg: getColor("background.overlay"),
            transform: "scale(1.1)"
          }}
          transition="all 0.2s"
        />
        <ModalBody pb={8} px={8} bg={getColor("background.card")}>
          <VStack spacing={4}>
            {!smsSent ? (
              <Box w="full">
                <FormControl>
                  <FormLabel 
                    color={getColor("primary")} 
                    fontWeight="600"
                    fontSize="md"
                    mb={3}
                  >
                    Phone Number
                  </FormLabel>
                  <VStack spacing={4} align="stretch">
                    <Select
                      value={selectedCountry.code}
                      onChange={(e) => handleCountryChange(e.target.value)}
                      bg={getColor("background.card")}
                      borderColor={getColor("border.medium")}
                      borderWidth="2px"
                      borderRadius="lg"
                      color={getColor("text.primary")}
                      fontSize="md"
                      h="12"
                      _hover={{ 
                        borderColor: getColor("primary"),
                        boxShadow: `0 0 0 1px ${getColor("primary")}30`
                      }}
                      _focus={{ 
                        borderColor: getColor("primary"), 
                        boxShadow: `0 0 0 3px ${getColor("primary")}20`
                      }}
                    >
                      {COUNTRY_CODES.map((country, index) => (
                        <option key={`${country.code}-${index}`} value={country.code}>
                          {country.flag} {country.country} ({country.code})
                        </option>
                      ))}
                    </Select>
                    
                    <InputGroup>
                      <InputLeftAddon 
                        bg={getColor("background.overlay")}
                        borderColor={getColor("border.medium")}
                        borderWidth="2px"
                        borderRadius="lg"
                        color={getColor("primary")}
                        fontWeight="600"
                        h="12"
                        minW="20"
                        fontSize="md"
                      >
                        {selectedCountry.code}
                      </InputLeftAddon>
                      <Input
                        type="tel"
                        value={localPhoneNumber}
                        onChange={(e) => setLocalPhoneNumber(e.target.value)}
                        placeholder={selectedCountry.placeholder}
                        autoComplete="tel"
                        name="tel"
                        inputMode="tel"
                        borderColor={getColor("border.medium")}
                        borderWidth="2px"
                        borderRadius="lg"
                        bg={getColor("background.card")}
                        color={getColor("text.primary")}
                        fontSize="md"
                        h="12"
                        _placeholder={{ color: getColor("text.muted") }}
                        _hover={{ 
                          borderColor: getColor("primary"),
                          boxShadow: `0 0 0 1px ${getColor("primary")}30`
                        }}
                        _focus={{ 
                          borderColor: getColor("primary"), 
                          boxShadow: `0 0 0 3px ${getColor("primary")}20`
                        }}
                      />
                    </InputGroup>
                  </VStack>
                  <FormHelperText mt={3}>
                    {formattedPhoneNumber ? (
                      <Box 
                        p={3} 
                        bg={`linear-gradient(135deg, ${getColor("status.success")}10, ${getColor("status.success")}05)`}
                        borderRadius="lg"
                        border="1px solid"
                        borderColor={`${getColor("status.success")}30`}
                      >
                        <HStack spacing={2}>
                          <Icon as={CheckIcon} color={getColor("status.success")} w={4} h={4} />
                          <Text color={getColor("status.success")} fontWeight="600" fontSize="sm">
                            Will be sent as: {formattedPhoneNumber}
                          </Text>
                        </HStack>
                      </Box>
                    ) : (
                      <Text color={getColor("text.muted")} fontSize="sm">
                        Enter your phone number without the country code
                        {selectedCountry.code === "+61" && " (Australian numbers: don't include the leading 0)"}
                      </Text>
                    )}
                  </FormHelperText>
                </FormControl>
                <Button
                  display="inline-flex"
                  alignItems="center"
                  gap={3}
                  px={8}
                  py={6}
                  minH="14"
                  fontSize="md"
                  fontWeight="600"
                  color="white"
                  bg={`linear-gradient(135deg, ${getColor("components.button.primaryBg")} 0%, ${getColor("secondary")} 100%)`}
                  borderRadius="xl"
                  transition="all 0.3s ease"
                  onClick={handleRequestAuthSms}
                  isLoading={requestLoading}
                  w="full"
                  mt={6}
                  position="relative"
                  overflow="hidden"
                  boxShadow={`0 10px 25px ${getColor("primary")}30`}
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                    transition: "left 0.5s",
                  }}
                  _hover={{
                    transform: "translateY(-3px)",
                    boxShadow: `0 15px 35px ${getColor("primary")}40`,
                    _before: {
                      left: "100%",
                    }
                  }}
                  _active={{
                    transform: "translateY(-1px)",
                  }}
                >
                  <Icon as={PhoneIcon} w={5} h={5} />
                  Send Verification SMS
                  <Box as="svg"
                    h={5}
                    w={5}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    transition="transform 0.3s ease"
                    _groupHover={{ transform: "translateX(4px)" }}
                  >
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                  </Box>
                </Button>
              </Box>
            ) : (
              <Box w="full">
                <Box 
                  p={4} 
                  bg={`linear-gradient(135deg, ${getColor("background.overlay")} 0%, ${getColor("background.light")} 100%)`}
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={getColor("border.light")}
                  mb={6}
                >
                  <Text 
                    color={getColor("text.primary")} 
                    fontWeight="500"
                    textAlign="center"
                    fontSize="md"
                  >
                    Please enter the verification words sent to your phone.
                  </Text>
                </Box>
                <FormControl>
                  <FormLabel 
                    color={getColor("primary")} 
                    fontWeight="600"
                    fontSize="md"
                    mb={3}
                  >
                    Verification Words
                  </FormLabel>
                  <Input
                    value={verificationWords}
                    onChange={(e) => setVerificationWords(e.target.value)}
                    placeholder="Enter verification words (e.g., revelation independent)"
                    borderColor={getColor("border.medium")}
                    borderWidth="2px"
                    borderRadius="lg"
                    bg={getColor("background.card")}
                    color={getColor("text.primary")}
                    fontSize="md"
                    h="12"
                    _placeholder={{ color: getColor("text.muted") }}
                    _hover={{ 
                      borderColor: getColor("primary"),
                      boxShadow: `0 0 0 1px ${getColor("primary")}30`
                    }}
                    _focus={{ 
                      borderColor: getColor("primary"), 
                      boxShadow: `0 0 0 3px ${getColor("primary")}20`
                    }}
                  />
                  <FormHelperText mt={3}>
                    {verificationWords.trim() ? (
                      <Box 
                        p={3} 
                        bg={`linear-gradient(135deg, ${getColor("status.success")}10, ${getColor("status.success")}05)`}
                        borderRadius="lg"
                        border="1px solid"
                        borderColor={`${getColor("status.success")}30`}
                      >
                        <HStack spacing={2}>
                          <Icon as={CheckIcon} color={getColor("status.success")} w={4} h={4} />
                          <Text color={getColor("status.success")} fontWeight="600" fontSize="sm">
                            Will be sent as: "{cleanVerificationWords(verificationWords)}"
                          </Text>
                        </HStack>
                      </Box>
                    ) : (
                      <Text color={getColor("text.muted")} fontSize="sm">
                        Enter the two words exactly as received in your SMS
                      </Text>
                    )}
                  </FormHelperText>
                </FormControl>
                <Button
                  display="inline-flex"
                  alignItems="center"
                  gap={3}
                  px={8}
                  py={6}
                  minH="14"
                  fontSize="md"
                  fontWeight="600"
                  color="white"
                  bg={`linear-gradient(135deg, ${getColor("components.button.primaryBg")} 0%, ${getColor("secondary")} 100%)`}
                  borderRadius="xl"
                  transition="all 0.3s ease"
                  onClick={handleVerifyAuthSms}
                  isLoading={verifyLoading}
                  isDisabled={!cleanVerificationWords(verificationWords).trim()}
                  w="full"
                  mt={6}
                  position="relative"
                  overflow="hidden"
                  boxShadow={`0 10px 25px ${getColor("primary")}30`}
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                    transition: "left 0.5s",
                  }}
                  _hover={{
                    transform: "translateY(-3px)",
                    boxShadow: `0 15px 35px ${getColor("primary")}40`,
                    _before: {
                      left: "100%",
                    }
                  }}
                  _active={{
                    transform: "translateY(-1px)",
                  }}
                  _disabled={{
                    bg: getColor("text.muted"),
                    color: getColor("text.inverse"),
                    cursor: "not-allowed",
                    opacity: 0.6,
                    _hover: {
                      transform: "none",
                      boxShadow: `0 10px 25px ${getColor("text.muted")}20`,
                      _before: {
                        left: "-100%",
                      }
                    }
                  }}
                >
                  <Icon as={CheckIcon} w={5} h={5} />
                  Verify & Login
                  <Box as="svg"
                    h={5}
                    w={5}
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    transition="transform 0.3s ease"
                    _groupHover={{ transform: "translateX(4px)" }}
                  >
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                  </Box>
                </Button>
              </Box>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}; 